// Word selection and board setup utilities for HintGrid.

import { shuffle } from "./game-utils";
import { MAX_CUSTOM_WORDS_ON_BOARD } from "./constants";
import { assignTeamsWithPlayabilityBias } from "./team-assignment";
import {
  CLASSIC_WORDS,
  GEOGRAPHY_WORDS,
  KAHOOT_WORDS,
  NATURE_WORDS,
  POP_CULTURE_WORDS,
  SCIENCE_WORDS,
  SPACE_WORDS,
  WORD_PACK_DEFINITIONS,
} from "./word-packs";
import type { WordPack } from "./types";

// Re-export WordPack type and pack constants for backwards compatibility.
export type { WordPack };
export {
  CLASSIC_WORDS,
  GEOGRAPHY_WORDS,
  KAHOOT_WORDS,
  NATURE_WORDS,
  POP_CULTURE_WORDS,
  SCIENCE_WORDS,
  SPACE_WORDS,
};

// Keep WORD_LIST as alias for backward compatibility.
export const WORD_LIST = CLASSIC_WORDS;

// Type alias for pack selection - single pack or array of packs.
type WordPackSelection = WordPack | WordPack[];

// Get the word list for a given pack.
export function getWordList(pack: WordPack = "classic"): string[] {
  return WORD_PACK_DEFINITIONS[pack]?.words ?? CLASSIC_WORDS;
}

// Combine multiple word packs into one deduplicated list.
export function getCombinedWordList(packs: WordPack[]): string[] {
  if (packs.length === 0) {
    return CLASSIC_WORDS;
  }

  if (packs.length === 1) {
    return getWordList(packs[0]);
  }

  const combined = new Set<string>();
  for (const pack of packs) {
    const words = getWordList(pack);
    for (const word of words) {
      combined.add(word);
    }
  }

  return Array.from(combined);
}

// Get display name for a word pack.
export function getPackDisplayName(pack: WordPack): string {
  const names: Record<WordPack, string> = {
    classic: "Classic",
    kahoot: "Kahoot",
    geography: "Geography",
    popculture: "Pop Culture",
    science: "Science",
    space: "Space",
    nature: "Nature",
  };
  return names[pack] ?? pack;
}

// Get display name for pack selection (single or multiple).
export function getPackSelectionDisplayName(selection: WordPackSelection): string {
  if (Array.isArray(selection)) {
    if (selection.length === 0) return "Classic";
    if (selection.length === 1) return getPackDisplayName(selection[0]);
    return selection.map(getPackDisplayName).join(" + ");
  }
  return getPackDisplayName(selection);
}

// Get all available word packs.
export function getAvailablePacks(): WordPack[] {
  return Object.keys(WORD_PACK_DEFINITIONS) as WordPack[];
}

// Get word count for a pack selection.
export function getWordCount(selection: WordPackSelection): number {
  if (Array.isArray(selection)) {
    return getCombinedWordList(selection).length;
  }
  return getWordList(selection).length;
}

/**
 * Generate a board from word packs and optional custom words.
 * Uses PRIORITY FILL: custom words fill first (up to MAX_CUSTOM_WORDS_ON_BOARD),
 * then pack words fill remaining slots.
 */
export function generateBoard(
  packs: WordPackSelection = "classic",
  customWords: string[] = []
): string[] {
  const packWords = Array.isArray(packs)
    ? getCombinedWordList(packs)
    : getWordList(packs);

  const shuffledCustom = shuffle([...customWords]);
  const customToUse = shuffledCustom.slice(0, MAX_CUSTOM_WORDS_ON_BOARD);
  const customSet = new Set(customToUse.map((word) => word.toUpperCase()));

  const filteredPacks = packWords.filter((word) => !customSet.has(word.toUpperCase()));
  const shuffledPacks = shuffle(filteredPacks);

  const slotsFromPacks = 25 - customToUse.length;
  const packWordsToUse = shuffledPacks.slice(0, slotsFromPacks);

  return shuffle([...customToUse, ...packWordsToUse]);
}

export function assignTeams(
  board: string[],
  startingTeam: "red" | "blue",
  packs?: WordPack[],
  semanticWords: string[] = []
): { word: string; team: "red" | "blue" | "neutral" | "trap" }[] {
  return assignTeamsWithPlayabilityBias(board, startingTeam, {
    packs,
    semanticWords,
  });
}
